/**
 * Affiliate and Coupon localStorage Management
 * 
 * Replaces the cookie-based approach with localStorage for:
 * - Affiliate referral tracking (30-day attribution)
 * - Coupon hints for auto-application
 * - Campaign and UTM parameter tracking
 */

import { getChatEntry } from './chatEntry';

const AFFILIATE_KEY = 'c_shaped_affiliate_ref';
const COUPON_HINT_KEY = 'c_shaped_coupon_hint';
const MARKETING_ATTRIBUTION_KEY = 'c_shaped_marketing_attribution';

export interface AffiliateData {
  affiliate_id: string;
  campaign?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  expires_at: number; // Unix timestamp
}

export interface CouponHint {
  code: string;
  expires_at: number; // Unix timestamp  
}

export interface MarketingAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  utm_id?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  landing_page_url: string;
  captured_at: number;
  expires_at: number;
}

const TRACKING_PARAMS = [
  'ref',
  'coupon',
  'campaign',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'adset_id',
  'ad_id',
  'fbclid',
];

const DISCARDED_AD_NETWORK_PARAMS = ['adset_id', 'ad_id', 'fbclid'];
// TikTok's click ID is consent-gated (see utils/tiktok.ts); never persist it
// through first-party attribution, which is stored without that consent.
const LANDING_URL_EXCLUDED_PARAMS = [...DISCARDED_AD_NETWORK_PARAMS, 'ttclid'];

function sanitizedLandingUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    LANDING_URL_EXCLUDED_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url.toString();
  } catch {
    return rawUrl;
  }
}

function sanitizeMarketingAttribution(data: any): MarketingAttribution {
  return {
    utm_source: data?.utm_source,
    utm_medium: data?.utm_medium,
    utm_campaign: data?.utm_campaign,
    utm_content: data?.utm_content,
    utm_term: data?.utm_term,
    utm_id: data?.utm_id,
    gclid: data?.gclid,
    wbraid: data?.wbraid,
    gbraid: data?.gbraid,
    landing_page_url: sanitizedLandingUrl(String(data?.landing_page_url || window.location.href)),
    captured_at: Number(data?.captured_at || Date.now()),
    expires_at: Number(data?.expires_at || Date.now()),
  };
}

function clearRetiredAdvertisingState(): void {
  try {
    window.localStorage.removeItem('meta_pixel_advanced_matching');
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('meta_complete_registration:'))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch { }

  try {
    const cookieNames = ['_fbp', '_fbc'];
    const host = window.location.hostname.replace(/^www\./, '');
    cookieNames.forEach((name) => {
      document.cookie = `${name}=;Max-Age=0;path=/;SameSite=Lax`;
      if (host && host !== 'localhost') {
        document.cookie = `${name}=;Max-Age=0;path=/;domain=.${host};SameSite=Lax`;
      }
    });
  } catch { }
}

/**
 * Parse URL parameters for affiliate and coupon tracking
 * Call this on app initialization and route changes
 */
export function handleLandingParams(): void {
  clearRetiredAdvertisingState();
  const urlParams = new URLSearchParams(window.location.search);
  const marketingParams = {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_id: urlParams.get('utm_id') || undefined,
    gclid: urlParams.get('gclid') || undefined,
    wbraid: urlParams.get('wbraid') || undefined,
    gbraid: urlParams.get('gbraid') || undefined,
  };
  const hasMarketingParams = Object.values(marketingParams).some(Boolean);
  const hasDiscardedAdNetworkParams = DISCARDED_AD_NETWORK_PARAMS
    .some((param) => urlParams.has(param));

  // Persist generic campaign and Google Ads attribution even when there is no
  // affiliate `ref`. Retired ad-network-specific fields are discarded.
  if (hasMarketingParams) {
    setMarketingAttribution({
      ...marketingParams,
      landing_page_url: sanitizedLandingUrl(window.location.href),
      captured_at: Date.now(),
      expires_at: Date.now() + (90 * 24 * 60 * 60 * 1000),
    });
  }
  
  // Handle affiliate referral
  const ref = urlParams.get('ref');
  if (ref) {
    setAffiliateRef({
      affiliate_id: ref,
      campaign: urlParams.get('campaign') || undefined,
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      utm_content: urlParams.get('utm_content') || undefined,
      utm_term: urlParams.get('utm_term') || undefined,
      expires_at: Date.now() + (30 * 24 * 60 * 60 * 1000) // 30 days
    });
  }
  
  // Handle coupon hint
  const coupon = urlParams.get('coupon');
  if (coupon) {
    setCouponHint({
      code: coupon.toUpperCase(),
      expires_at: Date.now() + (48 * 60 * 60 * 1000) // 48 hours
    });
  }
  
  // Keep Google's click IDs on the landing URL for the asynchronously loaded
  // Google tag. Local storage alone does not establish Google Ads attribution.
  if (ref || coupon || hasMarketingParams || hasDiscardedAdNetworkParams) {
    const cleanParams = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/chat') {
      const entry = getChatEntry(window.location.search);
      if (entry.domain) {
        cleanParams.set('domain', entry.domain);
        cleanParams.set('lang', entry.language);
      }
    }
    TRACKING_PARAMS.forEach((param) => cleanParams.delete(param));
    const query = cleanParams.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, document.title, cleanUrl);
  }
}

export function setMarketingAttribution(data: MarketingAttribution): void {
  try {
    localStorage.setItem(
      MARKETING_ATTRIBUTION_KEY,
      JSON.stringify(sanitizeMarketingAttribution(data)),
    );
  } catch (error) {
    console.warn('Failed to save marketing attribution data:', error);
  }
}

export function getMarketingAttribution(): MarketingAttribution | null {
  try {
    const stored = localStorage.getItem(MARKETING_ATTRIBUTION_KEY);
    if (!stored) return null;

    const data = sanitizeMarketingAttribution(JSON.parse(stored));
    if (data.expires_at < Date.now()) {
      localStorage.removeItem(MARKETING_ATTRIBUTION_KEY);
      return null;
    }
    localStorage.setItem(MARKETING_ATTRIBUTION_KEY, JSON.stringify(data));
    return data;
  } catch (error) {
    console.warn('Failed to read marketing attribution data:', error);
    localStorage.removeItem(MARKETING_ATTRIBUTION_KEY);
    return null;
  }
}

export function clearMarketingAttribution(): void {
  localStorage.removeItem(MARKETING_ATTRIBUTION_KEY);
}

/**
 * Set affiliate referral data with expiration
 */
export function setAffiliateRef(data: AffiliateData): void {
  try {
    localStorage.setItem(AFFILIATE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save affiliate referral data:', error);
  }
}

/**
 * Get affiliate referral data (null if expired or not found)
 */
export function getAffiliateRef(): AffiliateData | null {
  try {
    const stored = localStorage.getItem(AFFILIATE_KEY);
    if (!stored) return null;
    
    const data: AffiliateData = JSON.parse(stored);
    
    // Check if expired
    if (data.expires_at < Date.now()) {
      localStorage.removeItem(AFFILIATE_KEY);
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('Failed to read affiliate referral data:', error);
    localStorage.removeItem(AFFILIATE_KEY);
    return null;
  }
}

/**
 * Clear affiliate referral data
 */
export function clearAffiliateRef(): void {
  localStorage.removeItem(AFFILIATE_KEY);
}

/**
 * Set coupon hint for auto-application
 */
export function setCouponHint(hint: CouponHint): void {
  try {
    localStorage.setItem(COUPON_HINT_KEY, JSON.stringify(hint));
  } catch (error) {
    console.warn('Failed to save coupon hint:', error);
  }
}

/**
 * Get coupon hint (null if expired or not found)
 */
export function getCouponHint(): CouponHint | null {
  try {
    const stored = localStorage.getItem(COUPON_HINT_KEY);
    if (!stored) return null;
    
    const hint: CouponHint = JSON.parse(stored);
    
    // Check if expired
    if (hint.expires_at < Date.now()) {
      localStorage.removeItem(COUPON_HINT_KEY);
      return null;
    }
    
    return hint;
  } catch (error) {
    console.warn('Failed to read coupon hint:', error);
    localStorage.removeItem(COUPON_HINT_KEY);
    return null;
  }
}

/**
 * Restore campaign attribution and a coupon hint saved on a new account when
 * its email confirmation opens in a different browser. Existing values in
 * this browser always win.
 */
export function restoreSignupAttribution(
  utm: Partial<Record<'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term' | 'utm_id', string>> | null,
  landingPath?: string | null,
  coupon?: string | null,
): void {
  const now = Date.now();
  if (utm?.utm_source && !getMarketingAttribution()) {
    const path = landingPath && landingPath.startsWith('/') && !landingPath.startsWith('//') ? landingPath : '/';
    setMarketingAttribution({
      ...utm,
      landing_page_url: `${window.location.origin}${path}`,
      captured_at: now,
      expires_at: now + (90 * 24 * 60 * 60 * 1000),
    });
  }
  if (coupon && !getCouponHint()) {
    setCouponHint({ code: coupon.toUpperCase(), expires_at: now + (48 * 60 * 60 * 1000) });
  }
}

/**
 * Clear coupon hint (typically after successful application)
 */
export function clearCouponHint(): void {
  localStorage.removeItem(COUPON_HINT_KEY);
}

/**
 * Get attribution data for API calls
 * Combines affiliate referral data in the format expected by backend
 */
export function getAttributionData(): {
  affiliate_data?: AffiliateData;
  coupon_hint?: string;
  marketing_attribution?: MarketingAttribution;
} {
  const affiliateRef = getAffiliateRef();
  const couponHint = getCouponHint();
  const marketingAttribution = getMarketingAttribution();
  
  return {
    affiliate_data: affiliateRef || undefined,
    coupon_hint: couponHint?.code || undefined,
    marketing_attribution: marketingAttribution || undefined,
  };
}

/**
 * Track successful coupon application (clear hint to prevent re-application)
 */
export function trackCouponApplied(): void {
  clearCouponHint();
}

/**
 * Debug function to inspect current storage state
 */
export function debugStorageState(): {
  affiliate: AffiliateData | null;
  coupon: CouponHint | null;
  marketing: MarketingAttribution | null;
} {
  return {
    affiliate: getAffiliateRef(),
    coupon: getCouponHint(),
    marketing: getMarketingAttribution(),
  };
}

/**
 * Clear all affiliate and coupon data (for testing or user privacy)
 */
export function clearAllTrackingData(): void {
  clearAffiliateRef();
  clearCouponHint();
  clearMarketingAttribution();
}
